import { useCallback, useRef } from "react";
import type { HotTakeBankStatement } from "@somethings-ui/core/hot-takes";

const shuffle = <T,>(arr: readonly T[]): T[] => {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
};

export interface UseRandomHotTakes {
  getNext: (count: number) => HotTakeBankStatement[];
  shuffleOne: (currentId: string) => HotTakeBankStatement | null;
  markSeen: (id: string) => void;
  remainingCount: () => number;
}

// In-memory pool tracker scoped to a compose session. Avoids repeating a
// statement until the user has cycled through every available one, then
// resets and starts a fresh cycle so shuffling can continue indefinitely.
export const useRandomHotTakes = (
  pool: HotTakeBankStatement[],
): UseRandomHotTakes => {
  const seenIds = useRef<Set<string>>(new Set());

  // Pull `count` fresh picks from the pool. When the unseen set is too small
  // to cover the request, exhaust what's left and refill from a freshly-reset
  // cycle, excluding the just-picked ids so the same draw isn't repeated.
  const drawMany = useCallback(
    (count: number, alwaysExclude?: Set<string>): HotTakeBankStatement[] => {
      const exclude = new Set(alwaysExclude);
      const isAvailable = (s: HotTakeBankStatement) =>
        !seenIds.current.has(s.id) && !exclude.has(s.id);

      const out: HotTakeBankStatement[] = [];
      const firstPass = pool.filter(isAvailable);
      const fromFirst = shuffle(firstPass).slice(0, count);
      for (const s of fromFirst) {
        out.push(s);
        seenIds.current.add(s.id);
        exclude.add(s.id);
      }

      if (out.length < count) {
        // Cycled through pool — reset and pull the remainder from a fresh
        // cycle. `alwaysExclude` (e.g. the row being shuffled) stays barred.
        seenIds.current = new Set();
        const second = pool.filter(
          (s) => !exclude.has(s.id) && !seenIds.current.has(s.id),
        );
        const fromSecond = shuffle(second).slice(0, count - out.length);
        for (const s of fromSecond) {
          out.push(s);
          seenIds.current.add(s.id);
        }
      }

      return out;
    },
    [pool],
  );

  const getNext = useCallback(
    (count: number): HotTakeBankStatement[] => drawMany(count),
    [drawMany],
  );

  const shuffleOne = useCallback(
    (currentId: string): HotTakeBankStatement | null => {
      seenIds.current.add(currentId);
      const picks = drawMany(1, new Set([currentId]));
      return picks[0] ?? null;
    },
    [drawMany],
  );

  const markSeen = useCallback((id: string) => {
    seenIds.current.add(id);
  }, []);

  const remainingCount = useCallback(
    () => pool.length - seenIds.current.size,
    [pool],
  );

  return { getNext, shuffleOne, markSeen, remainingCount };
};
